import { Link, NavLink, Outlet, matchPath, useLocation } from "react-router-dom";

import { BackButton } from "../components/BackButton";
import { ModuleIcon } from "../components/ModuleIcon";
import { ProjectSelector } from "../components/ProjectSelector";
import { UserMenu } from "../components/UserMenu";
import { MODULES, MODULE_STATUS_LABELS, type ModuleDefinition } from "./modules";

type AppShellProps = {
  // The registry the sidebar is built from; tests pass their own.
  modules?: readonly ModuleDefinition[];
};

// True when the page open now is one of the module's further pages (activeOn, exact match).
function onFurtherPage(module: ModuleDefinition, pathname: string): boolean {
  return (module.activeOn ?? []).some(
    (path) => matchPath({ path, end: true }, pathname) !== null,
  );
}

// The sidebar renders every module from the registry so the product structure is
// always visible; only modules with a working page are links.
export function AppShell({ modules = MODULES }: AppShellProps = {}) {
  const { pathname } = useLocation();

  return (
    <div data-shell>
      <aside aria-label="Modules">
        <Link to="/" data-brand>
          KES Electrical OS
          <small>LV electrical design checks</small>
        </Link>
        <nav aria-label="Primary">
          <ul>
            <li>
              <NavLink to="/" end>
                <ModuleIcon name="Home" />
                <span data-module-code-label>Home</span>
                <span>Overview</span>
              </NavLink>
            </li>
            {modules.map((module) => {
              const statusLabel = MODULE_STATUS_LABELS[module.status];
              const body = (
                <>
                  <ModuleIcon name={module.icon} />
                  <span data-module-code-label>{module.code}</span>
                  <span>{module.name}</span>
                  <span data-module-status={statusLabel}>{statusLabel}</span>
                </>
              );
              return (
                <li key={module.code} data-module-code={module.code}>
                  {module.route && onFurtherPage(module, pathname) ? (
                    // NavLink marks only its own route as current; on a further page of the
                    // same module the link says so itself.
                    <Link to={module.route} aria-current="page">
                      {body}
                    </Link>
                  ) : module.route ? (
                    <NavLink to={module.route}>{body}</NavLink>
                  ) : (
                    <span aria-disabled="true">{body}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      <div data-shell-body>
        <header aria-label="Product">
          <BackButton />
          <span>Engineering design checks against referenced standard data</span>
          <ProjectSelector />
          <UserMenu />
        </header>

        <div data-shell-content>
          <Outlet />
        </div>

        <footer aria-label="Engineering basis">
          <p>
            Results are engineering design checks, not a statutory compliance certification, and
            require independent engineering review before use.
          </p>
          <p>
            Reference data status is shown with every result: Verified, Unverified or Unresolved.
            Product data is identified by class, specification and rating only; no manufacturer
            is named.
          </p>
        </footer>
      </div>
    </div>
  );
}
